import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Referral, ReferralFilters, ReferralStatus, ReferralStats } from '@/types/referral.types';
import { PaginatedResponse } from '@/types';
import { referralsService } from '@/services/referrals.service';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import {
  ReferralStatusBadge,
  getAllReferralStatuses,
  ReferralCard,
} from '@/components/referrals';

type TabType = 'sent' | 'received';

export const ReferralList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [meta, setMeta] = useState<PaginatedResponse<Referral>['meta'] | null>(null);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>(
    (searchParams.get('type') as TabType) || 'received'
  );

  // Filters
  const [statusFilter, setStatusFilter] = useState<ReferralStatus | ''>(
    (searchParams.get('status') as ReferralStatus) || ''
  );

  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);

  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingReferralId, setRejectingReferralId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch referrals
  const fetchReferrals = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters: ReferralFilters = {
        type: activeTab,
      };
      if (statusFilter) {
        filters.status = statusFilter;
      }

      const response = await referralsService.getAll(filters, page, 20);
      // API interceptor unwraps { success, data } - backend returns { data: [...], meta: {...} }
      const responseData = response.data;
      setReferrals(responseData?.data || []);
      setMeta(responseData?.meta || { total: 0, page: 1, limit: 20, totalPages: 0 });
    } catch (err) {
      setError('Failed to load referrals');
      console.error('Error fetching referrals:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, statusFilter, page]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      setIsLoadingStats(true);
      const response = await referralsService.getStats();
      // API interceptor unwraps { success, data } - backend returns stats directly
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('type', activeTab);
    if (statusFilter) params.set('status', statusFilter);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [activeTab, statusFilter, page, setSearchParams]);

  // Handle tab change
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setPage(1);
  };

  // Handle status filter change
  const handleStatusChange = (status: string) => {
    setStatusFilter(status as ReferralStatus | '');
    setPage(1);
  };

  // Handle accept referral
  const handleAccept = async (id: string) => {
    try {
      setIsSubmitting(true);
      await referralsService.accept(id);
      fetchReferrals();
      fetchStats();
    } catch (err) {
      console.error('Error accepting referral:', err);
      toast.error('Failed to accept referral');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle reject referral
  const openRejectModal = (id: string) => {
    setRejectingReferralId(id);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectingReferralId || !rejectionReason.trim()) return;

    try {
      setIsSubmitting(true);
      await referralsService.reject(rejectingReferralId, rejectionReason);
      setShowRejectModal(false);
      setRejectingReferralId(null);
      setRejectionReason('');
      fetchReferrals();
      fetchStats();
    } catch (err) {
      console.error('Error rejecting referral:', err);
      toast.error('Failed to reject referral');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel referral
  const handleCancel = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this referral?')) return;

    try {
      setIsSubmitting(true);
      await referralsService.cancel(id);
      fetchReferrals();
      fetchStats();
    } catch (err) {
      console.error('Error cancelling referral:', err);
      toast.error('Failed to cancel referral');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle complete referral
  const handleComplete = async (id: string) => {
    if (!confirm('Are you sure you want to mark this referral as completed?')) return;

    try {
      setIsSubmitting(true);
      await referralsService.complete(id);
      fetchReferrals();
      fetchStats();
    } catch (err) {
      console.error('Error completing referral:', err);
      toast.error('Failed to complete referral');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    ...getAllReferralStatuses(),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Referrals</h1>
          <p className="text-secondary-600 mt-1">
            Manage your sent and received referrals
          </p>
        </div>
        <Button
          onClick={() => navigate('/referrals/send')}
          leftIcon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          }
        >
          Send Referral
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card padding="sm" className="text-center">
          <p className="text-xs text-secondary-500 uppercase tracking-wider">Sent</p>
          <p className="text-2xl font-bold text-secondary-900 mt-1">
            {isLoadingStats ? '-' : stats?.sent.total || 0}
          </p>
        </Card>
        <Card padding="sm" className="text-center">
          <p className="text-xs text-secondary-500 uppercase tracking-wider">Received</p>
          <p className="text-2xl font-bold text-secondary-900 mt-1">
            {isLoadingStats ? '-' : stats?.received.total || 0}
          </p>
        </Card>
        <Card padding="sm" className="text-center">
          <p className="text-xs text-yellow-600 uppercase tracking-wider">Pending</p>
          <p className="text-2xl font-bold text-yellow-600 mt-1">
            {isLoadingStats ? '-' : (stats?.sent.pending || 0) + (stats?.received.pending || 0)}
          </p>
        </Card>
        <Card padding="sm" className="text-center">
          <p className="text-xs text-green-600 uppercase tracking-wider">Completed</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {isLoadingStats ? '-' : (stats?.sent.completed || 0) + (stats?.received.completed || 0)}
          </p>
        </Card>
        <Card padding="sm" className="text-center col-span-2 md:col-span-1">
          <p className="text-xs text-primary-600 uppercase tracking-wider">Total Earnings</p>
          <p className="text-2xl font-bold text-primary-600 mt-1">
            {isLoadingStats ? '-' : formatCurrency(stats?.total_earnings || 0)}
          </p>
        </Card>
      </div>

      {/* Tabs and Filters */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm">
        {/* Tabs */}
        <div className="border-b border-secondary-200">
          <nav className="flex" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('received')}
              className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'received'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              Received
              {stats && stats.received.pending > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-yellow-500 rounded-full">
                  {stats.received.pending}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('sent')}
              className={`flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'sent'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }`}
            >
              Sent
              {stats && stats.sent.pending > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-yellow-500 rounded-full">
                  {stats.sent.pending}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-secondary-200">
          <div className="flex items-center gap-4">
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="px-4 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {statusFilter && (
              <button
                onClick={() => setStatusFilter('')}
                className="px-3 py-2 text-sm text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 rounded-lg transition-colors"
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                <p className="text-secondary-600">Loading referrals...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-secondary-900 mb-2">Failed to load referrals</h3>
              <p className="text-secondary-600 mb-4">{error}</p>
              <Button onClick={fetchReferrals}>Try Again</Button>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && referrals.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary-100 mb-4">
                <svg className="w-8 h-8 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-secondary-900 mb-2">
                {statusFilter ? 'No referrals found' : `No ${activeTab} referrals yet`}
              </h3>
              <p className="text-secondary-600 mb-4">
                {activeTab === 'sent'
                  ? 'Start by sending a referral to another broker'
                  : 'Referrals you receive from other brokers will appear here'}
              </p>
              {activeTab === 'sent' && !statusFilter && (
                <Button onClick={() => navigate('/referrals/send')}>Send Referral</Button>
              )}
            </div>
          )}

          {/* Referrals List */}
          {!isLoading && !error && referrals.length > 0 && (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-hidden">
                <table className="min-w-full divide-y divide-secondary-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Referral
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Lead
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        {activeTab === 'sent' ? 'To Broker' : 'From Broker'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Split
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-200">
                    {referrals.map((referral) => {
                      const otherBroker = activeTab === 'sent' ? referral.to_broker : referral.from_broker;
                      const splitPercent = activeTab === 'sent' ? referral.sender_split_percent : referral.receiver_split_percent;

                      return (
                        <tr
                          key={referral.id}
                          onClick={() => navigate(`/referrals/${referral.id}`)}
                          className="hover:bg-secondary-50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-4 whitespace-nowrap">
                            <p className="text-sm font-medium text-secondary-900">
                              #{referral.referral_number}
                            </p>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <p className="text-sm font-medium text-secondary-900">{referral.lead.name}</p>
                            <p className="text-sm text-secondary-500">{referral.lead.phone}</p>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <p className="text-sm font-medium text-secondary-900">{otherBroker.full_name}</p>
                            {otherBroker.firm_name && (
                              <p className="text-sm text-secondary-500">{otherBroker.firm_name}</p>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-2 rounded-full overflow-hidden bg-secondary-200">
                                <div
                                  className={`h-full ${activeTab === 'sent' ? 'bg-primary-500' : 'bg-emerald-500'}`}
                                  style={{ width: `${splitPercent}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium text-secondary-700">{splitPercent}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <ReferralStatusBadge status={referral.status} size="sm" />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500">
                            {formatDate(referral.created_at)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                              {referral.status === 'PENDING' && activeTab === 'received' && (
                                <>
                                  <button
                                    onClick={() => handleAccept(referral.id)}
                                    disabled={isSubmitting}
                                    className="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
                                  >
                                    Accept
                                  </button>
                                  <button
                                    onClick={() => openRejectModal(referral.id)}
                                    disabled={isSubmitting}
                                    className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                              {referral.status === 'PENDING' && activeTab === 'sent' && (
                                <button
                                  onClick={() => handleCancel(referral.id)}
                                  disabled={isSubmitting}
                                  className="px-3 py-1 text-xs font-medium text-secondary-600 bg-secondary-100 hover:bg-secondary-200 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              )}
                              {referral.status === 'ACCEPTED' && (
                                <button
                                  onClick={() => handleComplete(referral.id)}
                                  disabled={isSubmitting}
                                  className="px-3 py-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  Complete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden grid gap-4 sm:grid-cols-2">
                {referrals.map((referral) => (
                  <ReferralCard
                    key={referral.id}
                    referral={referral}
                    type={activeTab}
                    onAccept={activeTab === 'received' ? handleAccept : undefined}
                    onReject={activeTab === 'received' ? openRejectModal : undefined}
                    onCancel={activeTab === 'sent' ? handleCancel : undefined}
                    onComplete={handleComplete}
                  />
                ))}
              </div>

              {/* Pagination */}
              {meta && meta.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-secondary-200">
                  <div className="flex items-center text-sm text-secondary-600">
                    Showing {((meta.page - 1) * meta.limit) + 1} to {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} referrals
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: meta.totalPages }, (_, i) => i + 1)
                        .filter((p) => p === 1 || p === meta.totalPages || Math.abs(p - page) <= 1)
                        .map((p, idx, arr) => (
                          <React.Fragment key={p}>
                            {idx > 0 && arr[idx - 1] !== p - 1 && (
                              <span className="px-2 text-secondary-400">...</span>
                            )}
                            <button
                              onClick={() => setPage(p)}
                              className={`w-8 h-8 text-sm font-medium rounded-lg transition-colors ${
                                p === page
                                  ? 'bg-primary-600 text-white'
                                  : 'text-secondary-600 hover:bg-secondary-100'
                              }`}
                            >
                              {p}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page === meta.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setShowRejectModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Reject Referral</h3>
            <p className="text-sm text-secondary-600 mb-4">
              Please provide a reason for rejecting this referral.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={3}
              className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg text-secondary-900 placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => setShowRejectModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleReject}
                disabled={!rejectionReason.trim() || isSubmitting}
                isLoading={isSubmitting}
              >
                Reject Referral
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralList;
